/**
 * The public module contract — the community surface.
 *
 * CREATE modules (exactly one active) own the in-progress stroke.
 * AFFECT modules (zero or more, stacked) transform the live shapes each frame.
 * This mirrors the original Alchemy create/affect split.
 */
import type { RGBA, Scene, Shape } from './scene';
import type { Rng } from './rng';

export type JSONValue = string | number | boolean | null | JSONValue[] | { [k: string]: JSONValue };

export type PointerType = 'mouse' | 'pen' | 'touch';

export interface InputSample {
  x: number;
  y: number;
  pressure: number; // 0..1
  tiltX: number; // degrees
  tiltY: number;
  twist: number; // degrees
  speed: number; // px per ms
  buttons: number;
  pointerType: PointerType;
  timeStamp: number;
}

export interface Viewport {
  width: number;
  height: number;
  dpr: number;
}

export interface AudioSnapshot {
  volume: number; // 0..1 RMS
  pitch: number | null; // Hz
}

/** Read access to the session swatch palette. Returned colours are fresh copies. */
export interface PaletteAccess {
  colors: readonly RGBA[];
  /** Swatch at index i, wrapping. */
  at(i: number): RGBA;
  /** A deterministic pick from the palette using the module's rng. */
  sample(rng: Rng): RGBA;
  /** Interpolated colour across the ordered swatches, t in [0,1]. */
  ramp(t: number): RGBA;
}

export interface ModuleContext {
  scene: Readonly<Scene>;
  input: InputSample;
  audio?: AudioSnapshot;
  viewport: Viewport;
  rng: Rng;
  /** The active ink, frozen for the duration of an in-progress stroke. */
  ink: RGBA;
  palette: PaletteAccess;
  config: Readonly<Record<string, JSONValue>>;
  newShape(init?: Partial<Shape>): Shape;
  drawGuide(shape: Shape): void;
  /** Ask the canvas to go dark while a stroke is in progress (Blindness). */
  requestBlind(): void;
}

export type ParamSpec =
  | { kind: 'slider'; key: string; label: string; min: number; max: number; step: number; default: number }
  | { kind: 'toggle'; key: string; label: string; default: boolean }
  | { kind: 'select'; key: string; label: string; options: { value: string; label: string }[]; default: string }
  | { kind: 'color'; key: string; label: string; default: string }
  | { kind: 'text'; key: string; label: string; default: string; placeholder?: string }
  | { kind: 'button'; key: string; label: string };

export type ModuleKind = 'create' | 'affect';

export type Capability = 'audio' | 'pressure' | 'tilt' | 'image' | 'random';

export interface ModuleManifest {
  id: string;
  kind: ModuleKind;
  name: string;
  description: string;
  icon?: string; // inline SVG markup
  params?: ParamSpec[];
  apiVersion: 1;
  capabilities?: Capability[];
}

export interface CreateModule {
  manifest: ModuleManifest;
  setup?(ctx: ModuleContext): void;
  onPointerDown?(s: InputSample, ctx: ModuleContext): void;
  onPointerMove?(s: InputSample, ctx: ModuleContext): void;
  onPointerUp?(s: InputSample, ctx: ModuleContext): void;
  /** Pointer moved with no button held (hover) — for inverted-interaction tools. */
  onHover?(s: InputSample, ctx: ModuleContext): void;
  onFrame?(ctx: ModuleContext): void;
  getLiveShapes(ctx: ModuleContext): Shape[];
  onCommit?(ctx: ModuleContext): void;
  onClear?(): void;
  onDeselect?(): void;
  onReselect?(): void;
}

export interface AffectModule {
  manifest: ModuleManifest;
  setup?(ctx: ModuleContext): void;
  /** The core hook: read upstream shapes, return the transformed set. */
  affect(shapes: readonly Shape[], ctx: ModuleContext): Shape[];
  onFrame?(ctx: ModuleContext): void;
  drawGuides?(ctx: ModuleContext): void;
  onCommit?(shapes: readonly Shape[], ctx: ModuleContext): Shape[];
  onClear?(): void;
  onDeselect?(): void;
}

export type AlcModule = CreateModule | AffectModule;

export function paramDefaults(manifest: ModuleManifest): Record<string, JSONValue> {
  const out: Record<string, JSONValue> = {};
  for (const p of manifest.params ?? []) {
    if (p.kind === 'button') continue;
    out[p.key] = p.default as JSONValue;
  }
  return out;
}

export function cfgNum(c: Readonly<Record<string, JSONValue>>, k: string, d: number): number {
  const v = c[k];
  return typeof v === 'number' ? v : d;
}

export function cfgStr(c: Readonly<Record<string, JSONValue>>, k: string, d: string): string {
  const v = c[k];
  return typeof v === 'string' ? v : d;
}

export function cfgBool(c: Readonly<Record<string, JSONValue>>, k: string, d: boolean): boolean {
  const v = c[k];
  return typeof v === 'boolean' ? v : d;
}
